"use client";

import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { useSubscriptions } from "@/context/SubscriptionContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Plus } from "lucide-react";
import {
  amountLabel,
  formatCurrency,
  generateTimeSlots,
  toBackendDiscount,
} from "@/lib/utils";
import { PriceAdjustmentInput } from "@/components/common/PriceAdjustmentInput";
import { toUTCString } from "@/lib/timezone";
import { VehicleType } from "@/types";
import { PaymentMode } from "@/types/enums.types";
import { toast } from "sonner";
import { format } from "date-fns";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { SelectSearchInput } from "@/components/common/SelectSearchInput";
import { serviceProviderService } from "@/services/serviceProviderService";
import { ServiceProvider } from "@/types";
import { VehicleMultiSelect } from "@/components/appointment/VehicleMultiSelect";
import { RepeatedAppointmentSelector } from "@/components/appointment/RepeatedAppointmentSelector";
import { RepeatedAppointmentWithDetails } from "@/types/repeatedAppointment.types";

const weekDays = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

// Generate time slots with 15-minute intervals (7:00 AM - 11:00 PM)
const timeSlots = generateTimeSlots();

// Helper to convert display time ("9:30 AM") to 24-hour value ("09:30")
const convertTimeToValue = (displayTime: string | null): string => {
  if (!displayTime) return "09:00";
  const normalized = displayTime.toLowerCase().replace(/\s+/g, "");
  const slot = timeSlots.find(
    (s) => s.label.toLowerCase().replace(/\s+/g, "") === normalized,
  );
  return slot ? slot.value : "09:00";
};

// Helper to get next occurrence of a weekday
const getNextDayOccurrence = (dayName: string): Date => {
  const days = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];
  const today = new Date();
  const targetDayIndex = days.indexOf(dayName.toLowerCase());
  if (targetDayIndex === -1) return today;

  const currentDayIndex = today.getDay();
  let daysUntilTarget = targetDayIndex - currentDayIndex;
  if (daysUntilTarget <= 0) daysUntilTarget += 7; // Next week if today or past

  const nextOccurrence = new Date(today);
  nextOccurrence.setDate(today.getDate() + daysUntilTarget);
  return nextOccurrence;
};

export default function NewAppointmentPage() {
  const navigate = useNavigate();
  const {
    customers,
    staff,
    oneTimePackages,
    subscriptionPlans,
    addOns,
    packagesLoading,
    packagesError,
    addAppointment,
    getVehiclesByCustomerId,
    addSubscription,
    addCustomer,
    addVehicle,
    subscriptions,
    getSubscriptionById,
    getActiveSubscriptionsByCustomerId,
    getSubscriptionsByCustomerId,
  } = useApp();

  // Get platinum subscription check from SubscriptionContext
  const { hasActivePlatinumSubscription } = useSubscriptions();

  // Booking type (UI: one-time | subscription)
  const [bookingType, setBookingType] = useState<"one-time" | "subscription">(
    "one-time",
  );

  // Form state
  const [formData, setFormData] = useState({
    customerId: "",
    vehicleIds: [] as string[],
    date: new Date(),
    time: "09:00",
    packageId: "",
    paymentMode: "CASH" as PaymentMode,
    address: "",
    staffId: "",
    // Optional backend bookingType: ONLINE | OFFLINE | SUBSCRIPTION
    // Admin panel appointments are OFFLINE by default (vs ONLINE from customer app)
    bookingType: "OFFLINE" as "ONLINE" | "OFFLINE" | "SUBSCRIPTION",
    // Subscription specific
    subscriptionId: "",
    // Repeated appointment
    isRepeatedAppointment: false,
    recurringDay: "Monday",
    preferredTime: "10:00 AM",
    // Subtotal (editable, initialized from package price)
    subtotal: 0,
    // Price adjustment, stored signed for the backend (see toBackendDiscount)
    discount: 0,
    // Booking Reference (optional - auto-generated if not provided)
    bookingRef: "",
  });

  // New customer/vehicle dialog state
  const [isNewCustomerDialogOpen, setIsNewCustomerDialogOpen] = useState(false);
  const [isNewVehicleDialogOpen, setIsNewVehicleDialogOpen] = useState(false);

  const [newCustomerData, setNewCustomerData] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    city: "",
    notes: "",
  });

  const [newVehicleData, setNewVehicleData] = useState({
    plateNumber: "",
    make: "",
    model: "",
    year: new Date().getFullYear(),
    color: "",
    type: "sedan" as VehicleType,
  });

  // Service Provider state
  const [providers, setProviders] = useState<ServiceProvider[]>([]);
  const [providerSearchTerm, setProviderSearchTerm] = useState("");
  const [providersKey, setProvidersKey] = useState(0);
  const [isCreatingProvider, setIsCreatingProvider] = useState(false);
  const [isCreatingAppointment, setIsCreatingAppointment] = useState(false);
  const [selectedServiceProviderId, setSelectedServiceProviderId] = useState<
    string | null
  >(null);

  // Customer and vehicle search terms
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [vehicleSearchTerm, setVehicleSearchTerm] = useState("");

  // Selected repeated appointment
  const [selectedRepeatedAppointmentId, setSelectedRepeatedAppointmentId] =
    useState<string | null>(null);

  // Track if user has manually edited subtotal
  const [isSubtotalManuallyEdited, setIsSubtotalManuallyEdited] =
    useState(false);

  // Get customer vehicles
  const customerVehicles = useMemo(() => {
    if (!formData.customerId) return [];
    return getVehiclesByCustomerId(formData.customerId);
  }, [formData.customerId, getVehiclesByCustomerId]);

  // Get all subscriptions for selected customer
  const availableSubscriptions = useMemo(() => {
    if (!formData.customerId) return [];
    return getSubscriptionsByCustomerId(formData.customerId);
  }, [formData.customerId, getSubscriptionsByCustomerId]);

  const filteredProviders = useMemo(() => {
    if (!providerSearchTerm.trim()) return providers;
    const q = providerSearchTerm.toLowerCase();
    return providers.filter((p) => p.name.toLowerCase().includes(q));
  }, [providers, providerSearchTerm]);

  const shouldShowAddProviderOption = useMemo(() => {
    if (!providerSearchTerm.trim()) return false;
    const q = providerSearchTerm.toLowerCase().trim();
    return !providers.some((p) => p.name.toLowerCase() === q);
  }, [providers, providerSearchTerm]);

  // Filter customers based on search
  const filteredCustomers = useMemo(() => {
    if (!customerSearchTerm.trim()) return customers;
    const searchLower = customerSearchTerm.toLowerCase();
    return customers.filter(
      (customer) =>
        customer.name.toLowerCase().includes(searchLower) ||
        (customer.phone && customer.phone.toLowerCase().includes(searchLower)),
    );
  }, [customers, customerSearchTerm]);

  // Filter vehicles based on search
  const filteredVehicles = useMemo(() => {
    if (!vehicleSearchTerm.trim()) return customerVehicles;
    const searchLower = vehicleSearchTerm.toLowerCase();
    return customerVehicles.filter(
      (vehicle) =>
        vehicle.plateNumber?.toLowerCase().includes(searchLower) ||
        String(vehicle.make || "")
          .toLowerCase()
          .includes(searchLower) ||
        String(vehicle.model || "")
          .toLowerCase()
          .includes(searchLower),
    );
  }, [customerVehicles, vehicleSearchTerm]);

  // Update subtotal when package or vehicle selection changes
  // Only auto-update if user hasn't manually edited the subtotal
  useEffect(() => {
    if (
      bookingType === "one-time" &&
      formData.packageId &&
      !isSubtotalManuallyEdited
    ) {
      const pkg = oneTimePackages.find((p) => p.id === formData.packageId);
      if (pkg) {
        // Multiply package price by number of selected vehicles
        const vehicleCount = Math.max(1, formData.vehicleIds.length);
        setFormData((prev) => ({
          ...prev,
          subtotal: pkg.price * vehicleCount,
        }));
      }
    }
  }, [
    bookingType,
    formData.packageId,
    formData.vehicleIds.length,
    oneTimePackages,
    isSubtotalManuallyEdited,
  ]);

  // Price adjustment (positive = surcharge / price increase, negative = discount / price reduction)
  const priceAdjustment = useMemo(() => {
    const val = typeof formData.discount === "number" ? formData.discount : parseFloat(formData.discount as any);
    return isNaN(val) ? 0 : val;
  }, [formData.discount]);

  // Check if customer has active Platinum subscription for 15% discount
  const isPlatinumCustomer = useMemo(() => {
    if (!formData.customerId || bookingType !== "one-time") return false;
    return hasActivePlatinumSubscription(formData.customerId);
  }, [formData.customerId, bookingType, hasActivePlatinumSubscription]);

  // Calculate platinum discount (15% of subtotal)
  const platinumDiscount = useMemo(() => {
    if (!isPlatinumCustomer || bookingType !== "one-time") return 0;
    return Math.round((formData.subtotal || 0) * 0.15 * 100) / 100;
  }, [isPlatinumCustomer, formData.subtotal, bookingType]);

  // Backend discount is the inverse of the UI adjustment (see toBackendDiscount)
  const effectiveDiscount = useMemo(() => {
    return toBackendDiscount(priceAdjustment);
  }, [priceAdjustment]);

  const totalAmount = useMemo(() => {
    if (bookingType === "subscription") {
      return 0; // Subscription appointments are included in subscription
    }

    if (bookingType === "one-time") {
      return Math.max(0, (formData.subtotal || 0) + priceAdjustment - platinumDiscount);
    }

    return 0;
  }, [bookingType, formData.subtotal, priceAdjustment, platinumDiscount]);

  // Handle new customer creation
  const handleCreateCustomer = () => {
    if (!newCustomerData.name || !newCustomerData.phone) {
      toast.error("Please fill in customer name and phone");
      return;
    }

    const customer = addCustomer({
      ...newCustomerData,
    });
    setFormData({ ...formData, customerId: customer.id, vehicleIds: [] });
    toast.success("Customer created successfully");
    setIsNewCustomerDialogOpen(false);
    setNewCustomerData({
      name: "",
      phone: "",
      email: "",
      address: "",
      city: "",
      notes: "",
    });
  };

  // Handle new vehicle creation
  const handleCreateVehicle = async () => {
    if (!formData.customerId) {
      toast.error("Please select a customer first");
      return;
    }

    if (
      !newVehicleData.plateNumber ||
      !newVehicleData.make ||
      !newVehicleData.model ||
      !newVehicleData.color
    ) {
      toast.error("Please fill in all required vehicle fields");
      return;
    }

    try {
      const vehicle = await addVehicle({
        ...newVehicleData,
        customerId: formData.customerId,
      });
      setFormData({
        ...formData,
        vehicleIds: [...formData.vehicleIds, vehicle.id],
      });
      setIsNewVehicleDialogOpen(false);
      setNewVehicleData({
        plateNumber: "",
        make: "",
        model: "",
        year: new Date().getFullYear(),
        color: "",
        type: "sedan",
      });
    } catch (error) {
      // Error already handled in addVehicle
    }
  };

  // Fetch service providers
  const fetchProviders = async () => {
    try {
      const res = await serviceProviderService.getAllServiceProviders();
      setProviders(res || []);
    } catch (err) {
      console.error("Failed to fetch providers:", err);
    }
  };

  useEffect(() => {
    fetchProviders();
  }, []);

  const handleCreateProvider = async (name: string) => {
    if (!name.trim() || isCreatingProvider) return;
    try {
      setIsCreatingProvider(true);
      const created = await serviceProviderService.createServiceProvider({
        name: name.trim(),
      });
      toast.success(`Provider "${created.name}" created`);
      await fetchProviders();
      setProvidersKey((p) => p + 1);
      setProviderSearchTerm("");
      setSelectedServiceProviderId(created.id);
    } catch (err: any) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to create provider");
    } finally {
      setIsCreatingProvider(false);
    }
  };

  // Handle repeated appointment selection - auto-populate fields
  const handleRepeatedAppointmentSelect = (
    setup: RepeatedAppointmentWithDetails | null,
  ) => {
    setSelectedRepeatedAppointmentId(setup?.id || null);
    if (setup) {
      // Calculate next occurrence date from preferred day
      const nextDate = setup.preferredDay
        ? getNextDayOccurrence(setup.preferredDay)
        : formData.date;

      // Convert time format from display ("9:30 AM") to value ("09:30")
      const timeValue = convertTimeToValue(setup.preferredTime);

      setFormData((prev) => ({
        ...prev,
        packageId: setup.packageId || "",
        vehicleIds: setup.vehicleIds,
        date: nextDate, // Set to next occurrence of preferred day
        time: timeValue, // Set time in 24-hour format
        recurringDay: setup.preferredDay
          ? setup.preferredDay.charAt(0).toUpperCase() +
            setup.preferredDay.slice(1)
          : prev.recurringDay,
        preferredTime: setup.preferredTime || prev.preferredTime,
      }));
    }
  };

  const handleCreate = async () => {
    // Vehicle is required unless bookingType is ONLINE
    // const isOnlineBooking = formData.bookingType === "ONLINE";

    if (!formData.customerId) {
      toast.error("Please select a customer");
      return;
    }

    if (formData.vehicleIds.length === 0) {
      toast.error("Please select at least one vehicle");
      return;
    }

    if (isCreatingAppointment) return; // Prevent double-clicks

    setIsCreatingAppointment(true);

    // Combine date and time into a single Date object
    const dateStr = format(formData.date, "yyyy-MM-dd");
    const combinedDateTime = new Date(`${dateStr}T${formData.time}`);

    try {
      if (bookingType === "one-time") {
        if (!formData.packageId) {
          toast.error("Please select a package");
          setIsCreatingAppointment(false);
          return;
        }

        const pkg = oneTimePackages.find((p) => p.id === formData.packageId);
        if (!pkg) {
          setIsCreatingAppointment(false);
          return;
        }

        // Create appointment with API structure
        await addAppointment({
          type: "PACKAGE",
          typeId: formData.packageId,
          // Properly convert local time to UTC ISO string for backend storage
          date: toUTCString(combinedDateTime),
          status: "PENDING",
          paymentMode: formData.paymentMode,
          amount: formData.subtotal || pkg.price,
          discount: effectiveDiscount,
          customerId: formData.customerId,
          vehicleIds: formData.vehicleIds,
          staffId: formData.staffId || null,
          serviceProviderId: selectedServiceProviderId || null,
          bookingType: formData.bookingType,
          address: formData.address || null,
          ruleId: null,
          bookingRef: formData.bookingRef || undefined,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          isDeleted: false,
          repeatedAppointmentId: selectedRepeatedAppointmentId || null,
          subtotal: formData.subtotal, // Subtotal override
          ...(formData.isRepeatedAppointment && {
            recurringDay: formData.recurringDay,
            preferredTime: formData.preferredTime,
          }),
        } as any);

        // Navigate after successful creation (toast is handled by context)
        navigate("/appointments");
      } else {
        // Subscription booking
        if (!formData.subscriptionId) {
          toast.error("Please select a subscription");
          setIsCreatingAppointment(false);
          return;
        }

        const subscription = getSubscriptionById(formData.subscriptionId);
        if (!subscription) {
          toast.error("Selected subscription not found");
          setIsCreatingAppointment(false);
          return;
        }

        // Validate subscription belongs to selected customer
        if (subscription.customerId !== formData.customerId) {
          toast.error(
            "Selected subscription does not belong to the selected customer",
          );
          setIsCreatingAppointment(false);
          return;
        }

        // Use subscription vehicle or selected vehicle (USER_SUBSCRIPTION supports single vehicle only)
        const vehicleId =
          subscription.vehicleId ||
          (formData.vehicleIds.length > 0 ? formData.vehicleIds[0] : null);
        if (!vehicleId) {
          toast.error("Please select a vehicle");
          setIsCreatingAppointment(false);
          return;
        }

        // Create appointment from existing subscription
        await addAppointment({
          type: "USER_SUBSCRIPTION",
          typeId: formData.subscriptionId, // Use subscription ID as typeId
          // Properly convert local time to UTC ISO string for backend storage
          date: toUTCString(combinedDateTime),
          status: "PENDING",
          paymentMode: formData.paymentMode,
          amount: null, // Included in subscription
          discount: effectiveDiscount,
          customerId: formData.customerId,
          vehicleIds: [vehicleId], // USER_SUBSCRIPTION uses single vehicle in array
          staffId: formData.staffId || null,
          serviceProviderId: selectedServiceProviderId || null,
          bookingType: formData.bookingType,
          address: formData.address || null,
          ruleId: null, // May need to get from subscription if available
          bookingRef: formData.bookingRef || undefined,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          isDeleted: false,
          repeatedAppointmentId: selectedRepeatedAppointmentId || null,
          ...(formData.isRepeatedAppointment && {
            recurringDay: formData.recurringDay,
            preferredTime: formData.preferredTime,
          }),
        } as any);

        // Navigate after successful creation (toast is handled by context)
        navigate("/appointments");
      }
    } catch (error) {
      // Error is already handled in context with toast
      console.error("Failed to create appointment:", error);
    } finally {
      setIsCreatingAppointment(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container max-w-4xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/appointments")}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              Create New Appointment
            </h1>
            <p className="text-muted-foreground">
              Schedule a new service or subscription
            </p>
          </div>
        </div>

        <Card>
          <CardContent className="pt-6 space-y-6">
            {/* Step 1: Booking Type */}
            <div className="space-y-4">
              <div>
                <Label className="text-base font-semibold">Booking Type</Label>
                <p className="text-sm text-muted-foreground">
                  One-time service booking
                </p>
              </div>
              <RadioGroup
                value={bookingType}
                onValueChange={(value: any) => {
                  setBookingType(value);
                  // Reset form fields when switching booking types
                  setFormData({
                    ...formData,
                    packageId: "",
                    subscriptionId: "",
                  });
                }}
              >
                <div className="grid grid-cols-2 gap-4">
                  <Card
                    className={
                      bookingType === "one-time" ? "border-primary" : ""
                    }
                  >
                    <CardContent className="pt-6">
                      <div className="flex items-start space-x-3">
                        <RadioGroupItem
                          value="one-time"
                          id="one-time"
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <Label
                            htmlFor="one-time"
                            className="text-base font-semibold cursor-pointer"
                          >
                            One-Time Service
                          </Label>
                          <p className="text-sm text-muted-foreground mt-1">
                            Single appointment for immediate service
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card
                    className={
                      bookingType === "subscription" ? "border-primary" : ""
                    }
                  >
                    <CardContent className="pt-6">
                      <div className="flex items-start space-x-3">
                        <RadioGroupItem
                          value="subscription"
                          id="subscription"
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <Label
                            htmlFor="subscription"
                            className="text-base font-semibold cursor-pointer"
                          >
                            Subscription
                          </Label>
                          <p className="text-sm text-muted-foreground mt-1">
                            Recurring service plan with monthly billing
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </RadioGroup>
            </div>

            <div className="border-t pt-6 space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4">
                  Customer & Vehicle Details
                </h3>
                <div className="grid gap-4">
                  {/* Customer Selection */}
                  <div className="grid gap-2">
                    <Label>Customer Name *</Label>
                    <div className="flex gap-2">
                      <Select
                        value={formData.customerId}
                        onValueChange={(value) => {
                          setFormData({
                            ...formData,
                            customerId: value,
                            vehicleIds: [],
                            subscriptionId: "",
                          });
                          setCustomerSearchTerm("");
                          setVehicleSearchTerm("");
                        }}
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder="Select customer" />
                        </SelectTrigger>
                        <SelectContent className="overflow-y-auto max-h-[400px]">
                          <SelectSearchInput
                            value={customerSearchTerm}
                            onChange={setCustomerSearchTerm}
                            placeholder="Search by name or phone..."
                          />
                          {filteredCustomers.length > 0 ? (
                            filteredCustomers.map((customer) => (
                              <SelectItem key={customer.id} value={customer.id}>
                                {customer.name} - {customer.phone || "No Phone"}
                              </SelectItem>
                            ))
                          ) : (
                            <div className="px-2 py-6 text-center text-sm text-muted-foreground">
                              No customers found
                            </div>
                          )}
                        </SelectContent>
                      </Select>
                      {/* <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setIsNewCustomerDialogOpen(true)}
                      >
                        <Plus className="h-4 w-4" />
                      </Button> */}
                    </div>
                  </div>

                  {/* Mobile # - Auto filled from customer */}
                  <div className="grid gap-2">
                    <Label>Mobile #</Label>
                    <Input
                      value={
                        formData.customerId
                          ? customers.find((c) => c.id === formData.customerId)
                              ?.phone || ""
                          : ""
                      }
                      disabled
                      className="bg-muted"
                    />
                  </div>

                  {/* Vehicle Selection - Multi-Select */}
                  <div className="grid gap-2">
                    <Label>Vehicle Details *</Label>
                    <VehicleMultiSelect
                      vehicles={filteredVehicles}
                      selectedVehicleIds={formData.vehicleIds}
                      onSelectionChange={(vehicleIds) =>
                        setFormData({ ...formData, vehicleIds })
                      }
                      disabled={!formData.customerId}
                      searchTerm={vehicleSearchTerm}
                      onSearchChange={setVehicleSearchTerm}
                    />
                  </div>

                  {/* Customer Type */}
                </div>
              </div>

              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4">Schedule Details</h3>
                <div className="grid gap-4">
                  {/* Booking Reference */}
                  <div className="grid gap-2">
                    <Label>Booking Ref No.</Label>
                    <Input
                      placeholder="e.g., WC-0001 (auto-generated if empty)"
                      value={formData.bookingRef}
                      onChange={(e) =>
                        setFormData({ ...formData, bookingRef: e.target.value })
                      }
                    />
                    <p className="text-xs text-muted-foreground">
                      Leave empty to auto-generate
                    </p>
                  </div>

                  {/* Date */}
                  <div className="grid gap-2">
                    <Label>Schedule Date *</Label>
                    <Input
                      type="date"
                      value={format(formData.date, "yyyy-MM-dd")}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          date: new Date(e.target.value),
                        })
                      }
                    />
                  </div>
                  {/* Time */}
                  <div className="grid gap-2">
                    <Label>Schedule Time *</Label>
                    <Select
                      value={formData.time || "09:00"}
                      onValueChange={(value) =>
                        setFormData({
                          ...formData,
                          time: value,
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select time" />
                      </SelectTrigger>
                      <SelectContent>
                        {timeSlots.map((slot) => (
                          <SelectItem key={slot.value} value={slot.value}>
                            {slot.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Address */}
                  <div className="grid gap-2">
                    <Label>Address</Label>
                    <Textarea
                      value={formData.address}
                      onChange={(e) =>
                        setFormData({ ...formData, address: e.target.value })
                      }
                      placeholder="Enter service address (optional)"
                      rows={3}
                    />
                  </div>
                  {/* Staff Assignment */}
                  <div className="grid gap-2">
                    <Label>Staff (Optional)</Label>
                    <Select
                      value={formData.staffId || undefined}
                      onValueChange={(value) =>
                        setFormData({ ...formData, staffId: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select staff member (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {staff.map((member) => (
                          <SelectItem key={member.id} value={member.id}>
                            {member.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Service Provider Selection */}
                  <div className="grid gap-2">
                    <Label>Service Provider (Optional)</Label>
                    <Select
                      key={`provider-select-${providersKey}-${providers.length}`}
                      value={selectedServiceProviderId || undefined}
                      onValueChange={(value) =>
                        setSelectedServiceProviderId(value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select service provider (optional)" />
                      </SelectTrigger>
                      <SelectContent
                        className="overflow-y-auto max-h-[300px]"
                        key={`provider-content-${providersKey}-${providers.length}`}
                      >
                        <SelectSearchInput
                          value={providerSearchTerm}
                          onChange={setProviderSearchTerm}
                          placeholder="Search provider..."
                        />
                        {filteredProviders.length > 0
                          ? filteredProviders.map((p) => (
                              <SelectItem key={p.id} value={p.id}>
                                {p.name}
                              </SelectItem>
                            ))
                          : !shouldShowAddProviderOption && (
                              <div className="px-2 py-6 text-center text-sm text-muted-foreground">
                                No providers found
                              </div>
                            )}
                        {shouldShowAddProviderOption && (
                          <div
                            className="px-2 py-2 border-t cursor-pointer hover:bg-accent"
                            onPointerDown={(e) => {
                              e.stopPropagation();
                              handleCreateProvider(providerSearchTerm);
                            }}
                          >
                            <div className="flex items-center gap-2 text-sm text-primary">
                              <span>+</span>
                              <span>
                                Add "{providerSearchTerm.trim()}" as new
                                provider
                              </span>
                            </div>
                          </div>
                        )}
                        <div className="px-2 py-2 border-t">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full justify-start h-auto py-2"
                            disabled={isCreatingProvider}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (providerSearchTerm.trim()) {
                                handleCreateProvider(providerSearchTerm);
                              } else {
                                toast.info(
                                  "Please enter provider name in the search field first",
                                );
                              }
                            }}
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            <span className="text-sm">Create New Provider</span>
                          </Button>
                        </div>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Repeated Appointment - Only shown for One-Time mode */}
                  {bookingType === "one-time" && (
                    <div className="grid gap-4 pt-4 border-t">
                      <div className="flex items-center space-x-3">
                        <Checkbox
                          id="repeated-appointment"
                          checked={formData.isRepeatedAppointment}
                          onCheckedChange={(checked) => {
                            setFormData({
                              ...formData,
                              isRepeatedAppointment: checked === true,
                            });
                            if (!checked) {
                              setSelectedRepeatedAppointmentId(null);
                            }
                          }}
                        />
                        <Label
                          htmlFor="repeated-appointment"
                          className="text-base font-medium cursor-pointer"
                        >
                          Repeated Appointment
                        </Label>
                      </div>

                      {formData.isRepeatedAppointment && (
                        <div className="pl-6 space-y-4 animate-in fade-in slide-in-from-top-2 duration-200">
                          {/* Repeated Appointment Selector */}
                          <RepeatedAppointmentSelector
                            customerId={formData.customerId}
                            packages={oneTimePackages}
                            customerVehicles={customerVehicles}
                            selectedSetupId={selectedRepeatedAppointmentId}
                            onSelect={handleRepeatedAppointmentSelect}
                            setupType="package"
                          />

                          {/* Recurring Day and Time (editable even after selection) */}
                          <div className="grid grid-cols-2 gap-4">
                            <div className="grid gap-2">
                              <Label htmlFor="recurringDay">
                                Recurring Day
                              </Label>
                              <Select
                                value={formData.recurringDay}
                                onValueChange={(value) =>
                                  setFormData({
                                    ...formData,
                                    recurringDay: value,
                                  })
                                }
                              >
                                <SelectTrigger id="recurringDay">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {weekDays.map((day) => (
                                    <SelectItem key={day} value={day}>
                                      {day}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            <div className="grid gap-2">
                              <Label htmlFor="preferredTime">
                                Preferred Time
                              </Label>
                              <Select
                                value={formData.preferredTime}
                                onValueChange={(value) =>
                                  setFormData({
                                    ...formData,
                                    preferredTime: value,
                                  })
                                }
                              >
                                <SelectTrigger id="preferredTime">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {timeSlots.map((slot) => (
                                    <SelectItem
                                      key={slot.value}
                                      value={slot.label}
                                    >
                                      {slot.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4">
                  Service Selection
                </h3>
                <div className="space-y-4">
                  {/* One-Time Package Selection */}
                  {bookingType === "one-time" && (
                    <>
                      <div className="grid gap-2">
                        <Label>Package *</Label>
                        {packagesLoading ? (
                          <div className="p-4 text-center text-muted-foreground">
                            Loading packages...
                          </div>
                        ) : packagesError ? (
                          <div className="p-4 text-center text-destructive">
                            {packagesError}
                          </div>
                        ) : (
                          <>
                            <Select
                              value={formData.packageId}
                              onValueChange={(value) => {
                                setIsSubtotalManuallyEdited(false); // Reset flag on package change
                                setFormData({ ...formData, packageId: value });
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select package" />
                              </SelectTrigger>
                              <SelectContent>
                                {oneTimePackages.filter(
                                  (pkg) => pkg.active !== false,
                                ).length === 0 ? (
                                  <div className="p-4 text-center text-muted-foreground">
                                    No packages available
                                  </div>
                                ) : (
                                  oneTimePackages
                                    .filter((pkg) => pkg.active !== false)
                                    .map((pkg) => (
                                      <SelectItem key={pkg.id} value={pkg.id}>
                                        <div className="flex items-center justify-between w-full">
                                          <span>{pkg.name}</span>
                                          <Badge
                                            variant="secondary"
                                            className="ml-2"
                                          >
                                            {formatCurrency(pkg.price)}
                                          </Badge>
                                        </div>
                                      </SelectItem>
                                    ))
                                )}
                              </SelectContent>
                            </Select>
                            {formData.packageId &&
                              (() => {
                                const selectedPackage = oneTimePackages.find(
                                  (p) => p.id === formData.packageId,
                                );
                                if (!selectedPackage) return null;

                                return (
                                  <div className="mt-4 p-4 bg-muted rounded-lg space-y-3">
                                    <div>
                                      <h4 className="font-semibold text-sm mb-1">
                                        Package Details
                                      </h4>
                                      <p className="text-sm text-muted-foreground">
                                        {selectedPackage.description}
                                      </p>
                                    </div>
                                    {selectedPackage.services &&
                                      selectedPackage.services.length > 0 && (
                                        <div>
                                          <h4 className="font-semibold text-sm mb-2">
                                            Services Included:
                                          </h4>
                                          <ul className="space-y-1">
                                            {selectedPackage.services.map(
                                              (service, index) => (
                                                <li
                                                  key={index}
                                                  className="text-sm flex items-start"
                                                >
                                                  <span className="mr-2">
                                                    •
                                                  </span>
                                                  <span>{service}</span>
                                                </li>
                                              ),
                                            )}
                                          </ul>
                                        </div>
                                      )}
                                    <div className="pt-2 border-t">
                                      <div className="flex justify-between items-center">
                                        <span className="text-sm font-medium">
                                          Package Price:
                                        </span>
                                        <span className="text-lg font-bold">
                                          {formatCurrency(
                                            selectedPackage.price,
                                          )}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()}
                          </>
                        )}
                      </div>
                    </>
                  )}

                  {bookingType === "subscription" && (
                    <div className="grid gap-2">
                      <Label>Subscription *</Label>
                      {!formData.customerId ? (
                        <div className="p-4 text-center text-muted-foreground">
                          Please select a customer first
                        </div>
                      ) : availableSubscriptions.length === 0 ? (
                        <div className="p-4 text-center text-destructive">
                          No subscriptions found for this customer
                        </div>
                      ) : (
                        <>
                          <Select
                            value={formData.subscriptionId}
                            onValueChange={(value) => {
                              const selectedSub = getSubscriptionById(value);
                              setFormData({
                                ...formData,
                                subscriptionId: value,
                                // Auto-select vehicle from subscription if available
                                vehicleIds: selectedSub?.vehicleId
                                  ? [selectedSub.vehicleId]
                                  : formData.vehicleIds,
                              });
                            }}
                            disabled={!formData.customerId}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select subscription" />
                            </SelectTrigger>
                            <SelectContent>
                              {availableSubscriptions.map((subscription) => {
                                const statusColors: Record<string, string> = {
                                  active: "bg-green-500 hover:bg-green-600",
                                  paused: "bg-yellow-500 hover:bg-yellow-600",
                                  cancelled: "bg-red-500 hover:bg-red-600",
                                  expired: "bg-gray-500 hover:bg-gray-600",
                                };
                                const statusColor =
                                  statusColors[subscription.status] || "";
                                return (
                                  <SelectItem
                                    key={subscription.id}
                                    value={subscription.id}
                                  >
                                    <div className="flex items-center justify-between w-full gap-2">
                                      <div className="flex items-center gap-2 flex-1 min-w-0">
                                        <span className="truncate">
                                          {subscription.planName}
                                        </span>
                                        <Badge
                                          className={statusColor}
                                          variant="outline"
                                        >
                                          {(() => {
                                            const s = String(
                                              subscription.status || "",
                                            );
                                            return s
                                              ? s.charAt(0).toUpperCase() +
                                                  s.slice(1)
                                              : "Unknown";
                                          })()}
                                        </Badge>
                                      </div>
                                      <Badge
                                        variant="secondary"
                                        className="ml-2 shrink-0"
                                      >
                                        {subscription.remainingServices}/
                                        {subscription.totalServices} washes
                                      </Badge>
                                    </div>
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                          {formData.subscriptionId && (
                            <div className="mt-2 p-4 bg-muted rounded-lg space-y-2">
                              {(() => {
                                const subscription = getSubscriptionById(
                                  formData.subscriptionId,
                                );
                                if (!subscription) return null;
                                return (
                                  <>
                                    <div className="flex gap-4 text-sm">
                                      <span>
                                        Remaining:{" "}
                                        {subscription.remainingServices}/
                                        {subscription.totalServices} washes
                                      </span>
                                    </div>
                                    <div className="flex gap-4 text-sm text-muted-foreground">
                                      <span>
                                        Exterior:{" "}
                                        {subscription.exteriorCleaningsUsed}/
                                        {subscription.exteriorCleaningsTotal}
                                      </span>
                                      <span>
                                        Interior:{" "}
                                        {subscription.interiorCleaningsUsed}/
                                        {subscription.interiorCleaningsTotal}
                                      </span>
                                    </div>
                                  </>
                                );
                              })()}
                            </div>
                          )}

                          {/* COMMENTED OUT: Repeated Setup for Subscription
                          {formData.subscriptionId && (
                            <div className="mt-4 pt-4 border-t">
                              <RepeatedAppointmentSelector
                                customerId={formData.customerId}
                                packages={oneTimePackages}
                                customerVehicles={customerVehicles}
                                selectedSetupId={selectedRepeatedAppointmentId}
                                onSelect={handleRepeatedAppointmentSelect}
                                setupType="subscription"
                                subscriptions={availableSubscriptions}
                              />
                            </div>
                          )}
                          */}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Mode */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4">Payment</h3>
                <div className="grid gap-4">
                  <div className="grid gap-2">
                    <Label>Payment Mode *</Label>
                    <Select
                      value={formData.paymentMode}
                      onValueChange={(value: PaymentMode) =>
                        setFormData({ ...formData, paymentMode: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CASH">Cash</SelectItem>
                        <SelectItem value="CREDIT_CARD">Credit Card</SelectItem>
                        <SelectItem value="DEBIT_CARD">Debit Card</SelectItem>
                        <SelectItem value="BANK_TRANSFER">
                          Bank Transfer
                        </SelectItem>
                        <SelectItem value="POS">POS</SelectItem>
                        <SelectItem value="APPLE_PAY">Apple Pay</SelectItem>
                        <SelectItem value="G_PAY">G-Pay</SelectItem>
                        <SelectItem value="WALLET">Wallet</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label>Booking Type (Optional)</Label>
                    <Select
                      value={formData.bookingType || undefined}
                      onValueChange={(value) =>
                        setFormData({
                          ...formData,
                          bookingType: value as
                            | "ONLINE"
                            | "OFFLINE"
                            | "SUBSCRIPTION",
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ONLINE">Online</SelectItem>
                        <SelectItem value="OFFLINE">Offline</SelectItem>
                        <SelectItem value="SUBSCRIPTION">
                          Subscription
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {bookingType === "one-time" && (
                    <div className="grid gap-2">
                      <Label>{amountLabel("Subtotal")}</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="Enter subtotal amount"
                        value={formData.subtotal || ""}
                        onChange={(e) => {
                          setIsSubtotalManuallyEdited(true);
                          setFormData({
                            ...formData,
                            subtotal: parseFloat(e.target.value) || 0,
                          });
                        }}
                      />
                      <p className="text-xs text-muted-foreground">
                        Pre-filled from package price. Edit to override.
                      </p>
                    </div>
                  )}
                  <PriceAdjustmentInput
                    value={priceAdjustment}
                    onChange={(val) =>
                      setFormData({ ...formData, discount: val })
                    }
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Price Breakdown Display */}
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="pt-6">
            {bookingType === "one-time" ? (
              <div className="space-y-3">
                {/* Platinum Member Badge */}
                {isPlatinumCustomer && (
                  <div className="flex items-center gap-2 p-2 bg-gradient-to-r from-violet-500/10 to-purple-500/10 rounded-lg border border-violet-500/20 mb-2">
                    <span className="text-lg">✨</span>
                    <span className="text-sm font-medium text-violet-700 dark:text-violet-300">
                      Platinum Member - 15% Discount Applied
                    </span>
                  </div>
                )}
                {/* Show per-vehicle breakdown when multiple vehicles selected */}
                {formData.vehicleIds.length > 1 &&
                  formData.packageId &&
                  (() => {
                    const pkg = oneTimePackages.find(
                      (p) => p.id === formData.packageId,
                    );
                    if (!pkg) return null;
                    return (
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-muted-foreground">
                          Package Price ({formData.vehicleIds.length} vehicles ×{" "}
                          {formatCurrency(pkg.price)})
                        </span>
                        <span>
                          {formatCurrency(
                            pkg.price * formData.vehicleIds.length,
                          )}
                        </span>
                      </div>
                    );
                  })()}
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>{formatCurrency(formData.subtotal || 0)}</span>
                </div>
                {/* Platinum Discount */}
                {isPlatinumCustomer && platinumDiscount > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">
                      Platinum Discount (15%)
                    </span>
                    <span className="text-violet-600 dark:text-violet-400 font-medium">
                      -{formatCurrency(platinumDiscount)}
                    </span>
                  </div>
                )}
                {/* Price Adjustment */}
                {typeof formData.discount === "number" && formData.discount !== 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">
                      {formData.discount > 0 ? "Price Adjustment (Surcharge)" : "Price Adjustment (Discount)"}
                    </span>
                    <span className={formData.discount > 0 ? "text-amber-600 dark:text-amber-400 font-medium" : "text-emerald-600 dark:text-emerald-400 font-medium"}>
                      {formData.discount > 0
                        ? `+${formatCurrency(formData.discount)}`
                        : `-${formatCurrency(Math.abs(formData.discount))}`}
                    </span>
                  </div>
                )}
                <div className="border-t pt-3 flex justify-between items-center">
                  <span className="text-lg font-semibold">Total Amount</span>
                  <span className="text-3xl font-bold">
                    {formatCurrency(totalAmount)}
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex justify-between items-center">
                <div>
                  <span className="text-lg font-semibold">Total Amount</span>
                  <p className="text-sm text-muted-foreground mt-1">
                    Included in subscription
                  </p>
                </div>
                <span className="text-3xl font-bold">{formatCurrency(0)}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pb-6">
          <Button
            variant="outline"
            onClick={() => navigate("/appointments")}
            size="lg"
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            size="lg"
            disabled={isCreatingAppointment}
          >
            {isCreatingAppointment ? "Creating..." : "Create Appointment"}
          </Button>
        </div>
      </div>

      {/* New Customer Dialog */}
      <Dialog
        open={isNewCustomerDialogOpen}
        onOpenChange={setIsNewCustomerDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Customer</DialogTitle>
            <DialogDescription>
              Add a new customer to the system
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="new-customer-name">Full Name *</Label>
              <Input
                id="new-customer-name"
                value={newCustomerData.name}
                onChange={(e) =>
                  setNewCustomerData({
                    ...newCustomerData,
                    name: e.target.value,
                  })
                }
                placeholder="John Doe"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="new-customer-phone">Phone *</Label>
              <Input
                id="new-customer-phone"
                value={newCustomerData.phone}
                onChange={(e) =>
                  setNewCustomerData({
                    ...newCustomerData,
                    phone: e.target.value,
                  })
                }
                placeholder="+974 XXXX XXXX"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="new-customer-email">Email</Label>
              <Input
                id="new-customer-email"
                type="email"
                value={newCustomerData.email}
                onChange={(e) =>
                  setNewCustomerData({
                    ...newCustomerData,
                    email: e.target.value,
                  })
                }
                placeholder="john@example.com"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="new-customer-address">Address</Label>
              <Input
                id="new-customer-address"
                value={newCustomerData.address}
                onChange={(e) =>
                  setNewCustomerData({
                    ...newCustomerData,
                    address: e.target.value,
                  })
                }
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="new-customer-city">City</Label>
              <Input
                id="new-customer-city"
                value={newCustomerData.city}
                onChange={(e) =>
                  setNewCustomerData({
                    ...newCustomerData,
                    city: e.target.value,
                  })
                }
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="new-customer-notes">Notes</Label>
              <Textarea
                id="new-customer-notes"
                value={newCustomerData.notes}
                onChange={(e) =>
                  setNewCustomerData({
                    ...newCustomerData,
                    notes: e.target.value,
                  })
                }
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsNewCustomerDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleCreateCustomer}>Create Customer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Vehicle Dialog */}
      <Dialog
        open={isNewVehicleDialogOpen}
        onOpenChange={setIsNewVehicleDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Vehicle</DialogTitle>
            <DialogDescription>
              Add a new vehicle for{" "}
              {customers.find((c) => c.id === formData.customerId)?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="new-vehicle-plate">Plate Number *</Label>
              <Input
                id="new-vehicle-plate"
                value={newVehicleData.plateNumber}
                onChange={(e) =>
                  setNewVehicleData({
                    ...newVehicleData,
                    plateNumber: e.target.value,
                  })
                }
                placeholder="ABC 123"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="new-vehicle-make">Make *</Label>
                <Input
                  id="new-vehicle-make"
                  value={newVehicleData.make}
                  onChange={(e) =>
                    setNewVehicleData({
                      ...newVehicleData,
                      make: e.target.value,
                    })
                  }
                  placeholder="Toyota"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="new-vehicle-model">Model *</Label>
                <Input
                  id="new-vehicle-model"
                  value={newVehicleData.model}
                  onChange={(e) =>
                    setNewVehicleData({
                      ...newVehicleData,
                      model: e.target.value,
                    })
                  }
                  placeholder="Camry"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="new-vehicle-year">Year *</Label>
                <Input
                  id="new-vehicle-year"
                  type="number"
                  value={newVehicleData.year}
                  onChange={(e) =>
                    setNewVehicleData({
                      ...newVehicleData,
                      year: parseInt(e.target.value),
                    })
                  }
                  min="1900"
                  max={new Date().getFullYear() + 1}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="new-vehicle-color">Color *</Label>
                <Input
                  id="new-vehicle-color"
                  value={newVehicleData.color}
                  onChange={(e) =>
                    setNewVehicleData({
                      ...newVehicleData,
                      color: e.target.value,
                    })
                  }
                  placeholder="White"
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="new-vehicle-type">Vehicle Type *</Label>
              <Select
                value={newVehicleData.type}
                onValueChange={(value: VehicleType) =>
                  setNewVehicleData({ ...newVehicleData, type: value })
                }
              >
                <SelectTrigger id="new-vehicle-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sedan">Sedan</SelectItem>
                  <SelectItem value="suv">SUV</SelectItem>
                  <SelectItem value="truck">Truck</SelectItem>
                  <SelectItem value="van">Van</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsNewVehicleDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleCreateVehicle}>Add Vehicle</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
